import "./App.css";
import "./styles/home-design.css";
import "./styles/arena.css";
import React, { Suspense, lazy, useEffect, useState } from "react";
import { Box, CssBaseline, ThemeProvider } from "@mui/material";
import { Navigate, Routes, Route, useLocation } from "react-router-dom";
import AppHeader from "./components/AppHeader";
import Sidebar from "./components/Sidebar";
import LobbyPage from "./pages/LobbyPage";
import SupportChat from "./components/SupportChat";
import GlobalChat from "./components/GlobalChat";
import MobileNav from "./components/MobileNav";
import WelcomeBonusModal from "./components/WelcomeBonusModal";
import BirthdayBonusModal from "./components/BirthdayBonusModal";
import SiteFooter from "./components/SiteFooter";
import AuthDialog from "./components/AuthDialog";
import SiteSeo from "./components/SiteSeo";
import MascotPopup from "./components/MascotPopup";
import theme from "./theme";
import { supportApi } from "./services/api";
import { useAuth } from "./auth/AuthContext";
import { isSfxMuted, setSfxMuted } from "./utils/sfx";
import { captureRef } from "./utils/refCode";
import MarsilyaLoader from "./components/MarsilyaLoader";

const PlayHubPage = lazy(() => import("./pages/PlayHubPage"));
const ProvidersPage = lazy(() => import("./pages/ProvidersPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const GamePlayPage = lazy(() => import("./pages/GamePlayPage"));
const AccountPage = lazy(() => import("./pages/AccountPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const WalletPage = lazy(() => import("./pages/WalletPage"));
const NotificationsPage = lazy(() => import("./pages/NotificationsPage"));
const DicePage = lazy(() => import("./pages/DicePage"));
const AviatorPage = lazy(() => import("./pages/AviatorPage"));
const BlackjackPage = lazy(() => import("./pages/BlackjackPage"));
const OriginalGamePage = lazy(() => import("./pages/OriginalGamePage"));
const RoulettePage = lazy(() => import("./pages/RoulettePage"));
const SportsPage = lazy(() => import("./pages/SportsPage"));
const CryptoPage = lazy(() => import("./pages/CryptoPage"));
const VipPage = lazy(() => import("./pages/VipPage"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const PageLoader = () => (
  <Box sx={{ display: "grid", placeItems: "center", minHeight: "40vh" }}>
    <MarsilyaLoader compact />
  </Box>
);

const Lazy = ({ children }) => <Suspense fallback={<PageLoader />}>{children}</Suspense>;

const StaffAdmin = () => {
  const { user, loading } = useAuth();
  useEffect(() => {
    if (loading) return;
    const role = user?.role;
    if (role === "founder") window.location.replace("/admin/en");
    else if (role === "admin" || role === "manager" || role === "sahaci") window.location.replace("/destek");
    else window.location.replace("/admin/en/signin");
  }, [loading, user]);
  return <PageLoader />;
};

const STAFF_DESK_ROLES = new Set(["founder", "admin", "manager", "sahaci"]);

const StaffDestekGate = () => {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (user && STAFF_DESK_ROLES.has(user.role)) {
    return <AdminPage />;
  }
  return <Navigate to="/" replace />;
};

const App = () => {
  const location = useLocation();
  const { user } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(() =>
    typeof window !== "undefined" && window.matchMedia("(min-width: 1101px)").matches,
  );
  const [search, setSearch] = useState("");
  const isGame = location.pathname.startsWith("/play/") || location.pathname.startsWith("/oyun/");
  const isDesk = location.pathname === "/destek" || location.pathname.startsWith("/destek/");

  useEffect(() => {
    if (typeof window !== "undefined") {
      setSidebarOpen(window.matchMedia("(min-width: 1101px)").matches);
    }
    if (location.pathname !== "/" && location.pathname !== "/games") {
      setSearch("");
    }
  }, [location.pathname]);

  useEffect(() => {
    captureRef(location.search);
  }, [location.search]);

  useEffect(() => {
    const onKey = (event) => {
      if (event.repeat || event.altKey || event.ctrlKey || event.metaKey) return;
      const tag = String(event.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select" || event.target?.isContentEditable) return;
      if (event.key !== "m" && event.key !== "M") return;
      event.preventDefault();
      setSfxMuted(!isSfxMuted());
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    const ping = () => supportApi.here(location.pathname).catch(() => {});
    ping();
    const timer = setInterval(ping, 12000);
    return () => clearInterval(timer);
  }, [user, location.pathname]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <SiteSeo />
      <Box
        className={`app-shell${isGame ? " is-game" : ""}${isDesk ? " is-desk" : ""}`}
      >
        {isDesk ? null : (
          <AppHeader
            search={search}
            onSearch={setSearch}
            onMenu={() => setSidebarOpen((open) => !open)}
          />
        )}
        <Box className="app-body">
          {isDesk ? null : (
            <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} onSearch={setSearch} />
          )}
          <Box className="app-main">
            <Box className={`app-content${isDesk ? " app-content-desk" : ""}`}>
              <Routes>
                <Route path="/" element={<LobbyPage search={search} onSearch={setSearch} />} />
                <Route path="/games" element={<LobbyPage search={search} onSearch={setSearch} />} />
                <Route path="/oyna" element={<Lazy><PlayHubPage /></Lazy>} />
                <Route path="/saglayicilar" element={<Lazy><ProvidersPage /></Lazy>} />
                <Route path="/spor" element={<Lazy><SportsPage /></Lazy>} />
                <Route path="/iddaa" element={<Navigate to="/spor" replace />} />
                <Route path="/espor" element={<Lazy><SportsPage /></Lazy>} />
                <Route path="/iletisim" element={<Lazy><ContactPage /></Lazy>} />
                <Route path="/login" element={<LobbyPage search={search} onSearch={setSearch} />} />
                <Route path="/register" element={<LobbyPage search={search} onSearch={setSearch} />} />
                <Route path="/play/:gameId" element={<Lazy><GamePlayPage /></Lazy>} />
                <Route path="/hesabim" element={<Lazy><AccountPage /></Lazy>} />
                <Route path="/bildirimler" element={<Lazy><NotificationsPage /></Lazy>} />
                <Route path="/profil" element={<Lazy><ProfilePage /></Lazy>} />
                <Route path="/cuzdan" element={<Lazy><WalletPage /></Lazy>} />
                <Route path="/vip" element={<Lazy><VipPage /></Lazy>} />
                <Route path="/rank" element={<Navigate to="/vip" replace />} />
                <Route path="/admin" element={<StaffAdmin />} />
                <Route path="/destek/*" element={<Lazy><StaffDestekGate /></Lazy>} />
                <Route path="/saha/*" element={<Navigate to="/destek" replace />} />
                <Route path="/oyun/zar" element={<Lazy><DicePage /></Lazy>} />
                <Route path="/oyun/aviator" element={<Lazy><AviatorPage /></Lazy>} />
                <Route path="/oyun/blackjack" element={<Lazy><BlackjackPage /></Lazy>} />
                <Route path="/oyun/okey" element={<Navigate to="/" replace />} />
                <Route path="/oyun/rulet" element={<Lazy><RoulettePage /></Lazy>} />
                <Route path="/oyun/kripto" element={<Lazy><CryptoPage /></Lazy>} />
                <Route path="/kripto" element={<Navigate to="/oyun/kripto" replace />} />
                <Route path="/oyun/:slug" element={<Lazy><OriginalGamePage /></Lazy>} />
                <Route path="/Wallet/*" element={<Navigate to="/cuzdan" replace />} />
                <Route path="/games/Roulette" element={<Navigate to="/oyun/rulet" replace />} />
                <Route path="*" element={<Lazy><NotFoundPage /></Lazy>} />
              </Routes>
              {isGame || isDesk ? null : <SiteFooter />}
            </Box>
          </Box>
        </Box>
        {isDesk ? null : <SupportChat />}
        {isDesk ? null : <GlobalChat />}
        {isGame || isDesk ? null : <MobileNav onBrowse={() => setSidebarOpen(true)} />}
        <WelcomeBonusModal />
        <BirthdayBonusModal />
        <AuthDialog />
        <MascotPopup />
      </Box>
    </ThemeProvider>
  );
};

export default App;
